import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Loader2, Wrench, X, XCircle } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useOnboardingState } from '@/contexts/OnboardingContext';
import { ApiError } from '@/lib/api';
import { HIT_AREA_44 } from '@/lib/touchTarget';
import { cn } from '@/lib/utils';
import {
  billingService,
  type BillingInterval,
  type BillingTier,
  type PlansResponse,
} from '@/services/billingService';

const CURRENCY_SYMBOL: Record<PlansResponse['currency'], string> = {
  BRL: 'R$',
  EUR: '€',
};

function formatPrice(currency: PlansResponse['currency'], amount: number): string {
  const formatter = new Intl.NumberFormat(currency === 'BRL' ? 'pt-BR' : 'pt-PT', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${CURRENCY_SYMBOL[currency]} ${formatter.format(amount)}`;
}

export function StripeSection() {
  const { t } = useTranslation();
  const { state, markStepCompleted, stripeOutcome, setStripeOutcome } = useOnboardingState();

  const stubAvailable = state?.stripe_stub_available ?? false;

  const [plans, setPlans] = useState<PlansResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [tier, setTier] = useState<BillingTier>('pro');
  const [interval, setIntervalValue] = useState<BillingInterval>('monthly');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // F158 AT8 — checkout 409 guard states. Distinct from submitError: these
  // are handled outcomes (stale UI, not a generic failure) and render their
  // own cards instead of the generic destructive submitError line.
  const [pastDue, setPastDue] = useState(false);
  const [alreadyActive, setAlreadyActive] = useState(false);
  const [portalBusy, setPortalBusy] = useState(false);
  const [portalError, setPortalError] = useState<string | null>(null);
  const [stubSubmitting, setStubSubmitting] = useState(false);
  const [stubError, setStubError] = useState<string | null>(null);

  // Intentionally empty deps: load plans once on mount. The ``t`` reference
  // returned by the react-i18next mock isn't stable across re-renders, so
  // adding it would re-fetch on every toggle click and trip the
  // ``toHaveBeenCalledTimes(1)`` assertion (+ thrash production traffic).
  // When the dev stub is available, skip the fetch — /plans would error with
  // no Stripe price env vars configured.
  useEffect(() => {
    if (stubAvailable) return;
    let cancelled = false;
    billingService
      .getPlans()
      .then((result) => {
        if (!cancelled) setPlans(result);
      })
      .catch((err) => {
        if (cancelled) return;
        const message = err instanceof ApiError ? err.message : 'onboarding.stripe.error_load';
        setLoadError(message);
      });
    return () => {
      cancelled = true;
    };
  }, [stubAvailable]);

  const tiers = useMemo<BillingTier[]>(() => ['pro', 'business'], []);

  async function handleContinue() {
    setSubmitError(null);
    setPastDue(false);
    setAlreadyActive(false);
    setSubmitting(true);
    try {
      const { url } = await billingService.createCheckoutSession({ tier, interval });
      window.location.assign(url);
    } catch (err) {
      setSubmitting(false);
      // F158 AT8 — branch on the guard's stable error.code BEFORE falling
      // through to the generic submitError path. Both are handled, benign
      // outcomes (stale UI), not failures — they get their own cards.
      if (err instanceof ApiError && err.code === 'subscription_past_due') {
        setPastDue(true);
        return;
      }
      if (err instanceof ApiError && err.code === 'subscription_already_active') {
        setAlreadyActive(true);
        return;
      }
      const message = err instanceof ApiError ? err.message : t('onboarding.stripe.error_checkout');
      setSubmitError(message);
    }
  }

  async function handlePastDuePortal() {
    setPortalBusy(true);
    setPortalError(null);
    try {
      const { url } = await billingService.getCustomerPortalUrl();
      window.location.assign(url);
    } catch (err) {
      setPortalBusy(false);
      const message = err instanceof ApiError ? err.message : t('onboarding.stripe.error_checkout');
      setPortalError(message);
    }
  }

  async function handleDevStub() {
    setStubError(null);
    setStubSubmitting(true);
    try {
      await markStepCompleted('stripe_checkout');
    } catch {
      setStubSubmitting(false);
      setStubError(t('onboarding.stripe.dev_stub.error'));
    }
  }

  // ----- dev stub (DEBUG mode, no Stripe API key) -----
  if (stubAvailable) {
    return (
      <div className="space-y-3" data-testid="section-stripe">
        <div
          className="rounded-md border border-warning/50 bg-warning/10 p-4 text-sm text-warning-foreground"
          data-testid="stripe-dev-stub-card"
        >
          <p className="inline-flex items-center gap-1.5 font-medium">
            <Wrench className="h-4 w-4 shrink-0" aria-hidden="true" />
            {t('onboarding.stripe.dev_stub.title')}
          </p>
          <p className="mt-1 text-muted-foreground">{t('onboarding.stripe.dev_stub.body')}</p>
        </div>
        {stubError && (
          <p className="text-sm text-destructive" role="alert">
            {stubError}
          </p>
        )}
        <Button
          type="button"
          onClick={handleDevStub}
          disabled={stubSubmitting}
          className="min-h-11"
          data-testid="stripe-dev-stub-button"
        >
          {stubSubmitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              {t('onboarding.stripe.dev_stub.button')}
            </>
          ) : (
            t('onboarding.stripe.dev_stub.button')
          )}
        </Button>
      </div>
    );
  }

  if (loadError) {
    return (
      <div
        className="rounded-md border border-destructive/50 bg-destructive/5 p-4 text-sm"
        role="alert"
        data-testid="stripe-load-error"
      >
        <p className="font-medium text-destructive">{loadError}</p>
      </div>
    );
  }

  if (!plans) {
    return (
      <div
        className="flex items-center gap-2 text-sm text-muted-foreground"
        data-testid="stripe-loading"
      >
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        {t('common.loading')}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="section-stripe">
      {stripeOutcome.kind === 'cancelled' && (
        <div
          className="flex items-start gap-2 rounded-md border border-warning/50 bg-warning/10 p-3 text-sm text-warning-foreground"
          role="status"
          data-testid="stripe-cancelled-banner"
        >
          <XCircle className="h-4 w-4 mt-0.5 text-warning" aria-hidden="true" />
          <div>
            <p className="font-medium">{t('onboarding.stripe.cancelled.title')}</p>
            <p className="text-muted-foreground mt-0.5">{t('onboarding.stripe.cancelled.body')}</p>
          </div>
          <button
            type="button"
            onClick={() => setStripeOutcome({ kind: 'idle' })}
            className={cn('ml-auto text-muted-foreground hover:text-foreground', HIT_AREA_44)}
            aria-label={t('common.cancel')}
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      )}
      {stripeOutcome.kind === 'timed_out' && (
        <div
          className="flex items-start gap-2 rounded-md border border-warning/50 bg-warning/10 p-3 text-sm text-warning-foreground"
          role="status"
          data-testid="stripe-timed-out-banner"
        >
          <Loader2 className="h-4 w-4 mt-0.5 text-warning" aria-hidden="true" />
          <div>
            <p className="font-medium">{t('onboarding.stripe.timed_out.title')}</p>
            <p className="text-muted-foreground mt-0.5">{t('onboarding.stripe.timed_out.body')}</p>
          </div>
        </div>
      )}

      {/* F158 AT8 — checkout guard 409 states. Plan cards + continue button
          stay visible underneath both cards so the tenant is never dead-ended. */}
      {pastDue && (
        <div
          className="rounded-md border border-destructive/50 bg-destructive/5 p-4 text-sm"
          role="alert"
          data-testid="stripe-past-due-card"
        >
          <p className="font-medium text-destructive">{t('onboarding.stripe.past_due.title')}</p>
          <p className="mt-1 text-muted-foreground">{t('onboarding.stripe.past_due.body')}</p>
          {portalError && (
            <p className="mt-2 text-destructive" role="alert">
              {portalError}
            </p>
          )}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handlePastDuePortal}
            disabled={portalBusy}
            className="mt-3 min-h-11"
            data-testid="stripe-past-due-portal-btn"
          >
            {portalBusy ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                {t('onboarding.stripe.past_due.cta')}
              </>
            ) : (
              t('onboarding.stripe.past_due.cta')
            )}
          </Button>
        </div>
      )}

      {alreadyActive && (
        <div
          className="flex items-start gap-2 rounded-md border border-success/30 bg-success/15 p-3 text-sm text-success-foreground"
          role="status"
          data-testid="stripe-already-active-info"
        >
          <Check className="h-4 w-4 mt-0.5" aria-hidden="true" />
          <div>
            <p className="font-medium">{t('onboarding.stripe.already_active.title')}</p>
            <p className="mt-0.5 text-muted-foreground">
              {t('onboarding.stripe.already_active.body')}
            </p>
          </div>
        </div>
      )}

      <p className="text-sm text-muted-foreground">
        {t('onboarding.stripe.body', {
          days: plans.trial.days,
          messages: plans.trial.message_cap,
        })}
      </p>

      <div
        className="inline-flex rounded-md border bg-muted/40 p-0.5"
        role="tablist"
        aria-label={t('onboarding.stripe.interval_label')}
      >
        <button
          type="button"
          role="tab"
          aria-selected={interval === 'monthly'}
          onClick={() => setIntervalValue('monthly')}
          data-testid="stripe-interval-monthly"
          className={`min-h-11 rounded px-3 text-sm transition-colors ${
            interval === 'monthly'
              ? 'bg-background shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          {t('onboarding.stripe.monthly')}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={interval === 'quarterly'}
          onClick={() => setIntervalValue('quarterly')}
          data-testid="stripe-interval-quarterly"
          className={`min-h-11 rounded px-3 text-sm transition-colors ${
            interval === 'quarterly'
              ? 'bg-background shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          {t('onboarding.stripe.quarterly')}
        </button>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {tiers.map((option) => {
          const selected = tier === option;
          const pricing = plans.plans[option][interval];
          return (
            <button
              type="button"
              key={option}
              data-testid={`stripe-plan-card-${option}`}
              onClick={() => setTier(option)}
              className={`rounded-lg border p-4 text-left transition-colors min-h-44 ${
                selected
                  ? 'border-foreground bg-card'
                  : 'border-border bg-card hover:border-foreground/40'
              }`}
              aria-pressed={selected}
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">
                  {t(`onboarding.stripe.tier.${option}.name`)}
                </span>
                {selected && (
                  <span
                    className="inline-flex items-center text-success"
                    data-testid={`stripe-plan-select-${option}`}
                  >
                    <Check className="h-4 w-4" aria-hidden="true" />
                  </span>
                )}
                {!selected && (
                  <span
                    className="text-xs text-muted-foreground"
                    data-testid={`stripe-plan-select-${option}`}
                  >
                    {t('onboarding.stripe.select')}
                  </span>
                )}
              </div>
              <p className="mt-3 text-xl font-semibold" data-testid={`stripe-price-${option}`}>
                {formatPrice(plans.currency, pricing.price_amount)}
                <span className="text-xs font-normal text-muted-foreground ml-1">
                  /{t(`onboarding.stripe.${interval}_short`)}
                </span>
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {t(`onboarding.stripe.tier.${option}.summary`)}
              </p>
            </button>
          );
        })}
      </div>

      {submitError && (
        <p className="text-sm text-destructive" role="alert">
          {submitError}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <Button
          type="button"
          onClick={handleContinue}
          disabled={submitting}
          data-testid="stripe-continue-button"
          className="min-h-11"
        >
          {submitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              {t('onboarding.stripe.redirecting')}
            </>
          ) : (
            t('onboarding.stripe.continue')
          )}
        </Button>
        <p className="text-xs text-muted-foreground">{t('onboarding.stripe.trust')}</p>
      </div>
    </div>
  );
}
